import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'
import { Link } from 'react-router'
import classNames from 'classnames'
import { excerptify } from '@raywhite/helpers-utils/lib/helpers/string'
import { depressify, plainText } from '@raywhite/helpers-utils/lib/helpers/sanitization'
import { newsArticle as newsArticleSchema } from '@raywhite/helpers-utils/lib/helpers/structuredData'
import { createTimeSince } from '@raywhite/helpers-utils/lib/helpers/time'
import {
  format as formatUrl,
  parse as parseUrl,
} from '../../utils/url'
import DeferredImage from './DeferredImage.jsx'
import { getItemImage, getItemAuthor } from '../../utils/wordpress/content'
import ShareLinks from './ShareLinks.jsx'
import ContentCardList from './ContentCardList.jsx'
import JsonLD from './JsonLD.jsx'
import TwitterSummaryCard from './TwitterSummaryCard.jsx'
import OpenGraph from './OpenGraph.jsx'

export default class NewsContentItem extends Component {
  static propTypes = {
    content: PropTypes.object.isRequired,
    isCommercial: PropTypes.bool.isRequired,
    showSharing: PropTypes.bool.isRequired,
    showDate: PropTypes.bool.isRequired,
    children: PropTypes.node,
    typeName: PropTypes.string,
    primaryDomain: PropTypes.string.isRequired,
    protocol: PropTypes.string.isRequired,
    twitter: PropTypes.string,
    className: PropTypes.string,
    recentNews: PropTypes.object.isRequired,
    nextItem: PropTypes.object,
    prevItem: PropTypes.object,
  }

  static defaultProps = {
    showSharing: false,
    showDate: false,
  }

  render() {
    const {
      content,
      isCommercial,
      showSharing,
      showDate,
      typeName,
      primaryDomain,
      protocol,
      twitter,
      className,
      recentNews,
      nextItem,
      prevItem,
      children,
    } = this.props
    const image = getItemImage(content)
    const author = getItemAuthor(content)
    const _content = content.content ? content.content.rendered : ''
    const body = depressify(_content)
    const parts = parseUrl(content.link)
    const url = formatUrl({ ...parts, protocol, hostname: primaryDomain })

    const rawTitle = content.title && content.title.rendered
    const safeTitle = rawTitle && plainText(rawTitle)
    const seoTitle = content.seo_title || safeTitle

    const structuredData = newsArticleSchema({
      headline: seoTitle,
      articleSection: typeName,
      articleBody: body && plainText(body.__html),
      url,
      image: image && `${image.url}?maxwidth=1280`,
      datePublished: content.date,
      dateModified: content.modified,
    })
    const metaDescription = content.excerpt
      ? plainText(content.excerpt.rendered)
      : structuredData.articleBody

    // Recent news: the two most recent items that aren't the current item
    const recents = recentNews.entities
      .filter(item => item.id !== content.id)
      .slice(0, 2)

    return (
      <div
        className={classNames('sgl_news', className, {
          [`${content.slug.replace(/-/g, '_')}_pg`]: true,
        })}
      >
        <Helmet>
          <title>{typeName ? `${seoTitle} - ${typeName}` : seoTitle}</title>
          <meta name="description" content={excerptify(metaDescription, 160)} />
        </Helmet>
        <JsonLD>{structuredData}</JsonLD>
        <TwitterSummaryCard
          title={safeTitle}
          description={metaDescription}
          image={structuredData.image}
          site={twitter}
        />
        <OpenGraph
          mayCropImage
          type="article"
          url={url}
          image={image}
          title={safeTitle}
          description={metaDescription}
          article={{
            modifiedTime: structuredData.datePublished,
            publishedTime: structuredData.dateModified,
            section: structuredData.articleSection,
          }}
        />
        <div className={`pg_header sgl_news_header ${image ? 'has_feature' : ''}`}>
          <div className="inner_md">
            {!!typeName && (
              <span className="mini muted preheading">{typeName}</span>
            )}
            <h1
              className="charlie centered_text"
              dangerouslySetInnerHTML={{
                __html: rawTitle,
              }}
            />
            {showDate && !!content.date_gmt && (
              <div className="sgl_news_meta mini muted">
                <time>{createTimeSince(new Date().getTime(), content.date_gmt)}</time>
                {!!author && ([
                  <span key="sep" className="sgl_news_meta_bull">&bull;</span>,
                  <span key="author" className="sgl_news_author">
                    {`By ${author}`}
                  </span>,
                ])}
              </div>
            )}
            {showSharing && (
              <div className="sgl_news_share primary centered_text show_charlie">
                <ShareLinks
                  id={content.id}
                  url={url}
                  linkedIn={isCommercial}
                />
              </div>
            )}
          </div>
        </div>
        {image && (
          <div className="inner_md">
            <div className="sgl_news_feature">
              <div
                className="responsive_image_wrapper_wrapper"
                style={{ maxWidth: image.width }}
              >
                <DeferredImage
                  image={image}
                  attrs={{
                    width: 750,
                    scale: 'both',
                  }}
                />
              </div>
            </div>
          </div>
        )}
        <div className="inner_md sgl_news_content_wrap">
          <div
            className="sgl_news_content"
            dangerouslySetInnerHTML={body}
          />
          {children}
        </div>

        {showSharing && (
          <div className="sgl_news_share secondary centered_text">
            <strong>Share:</strong>
            <ShareLinks
              id={content.id}
              url={url}
              linkedIn={isCommercial}
            />
          </div>
        )}

        {!!(nextItem || prevItem) && (
          <div className="inner sgl_news_pagination_wrap">
            <div className="sgl_news_pagination">
              {!!prevItem && (
                <div className="prev">
                  <Link to={parseUrl(prevItem.link).path}>
                    <span className="muted">Prev Post</span>
                    <h4
                      className="echo"
                      dangerouslySetInnerHTML={{
                        __html: prevItem.title && prevItem.title.rendered,
                      }}
                    />
                  </Link>
                </div>
              )}
              {!!nextItem && (
                <div className="next">
                  <Link to={parseUrl(nextItem.link).path}>
                    <span className="muted">Next Post</span>
                    <h4
                      className="echo"
                      dangerouslySetInnerHTML={{
                        __html: nextItem.title && nextItem.title.rendered,
                      }}
                    />
                  </Link>
                </div>
              )}
            </div>
            <div className="centered_text">
              <Link to="/news" className="anchor">Back to all news</Link>
            </div>
          </div>
        )}

        {!!recents.length && (
          <section className="dark_bg centered_text sgl_news_latest">
            <span className="muted mini preheading">Up to Date</span>
            <h2 className="charlie">Latest News</h2>
            <div className="home_content_wrap">
              <ContentCardList
                typeName="News"
                className="sgl_news_latest_list_wrap"
                listClassName="inner_lg"
                loaded
                entities={recents}
                cardOptions={{ showImage: false }}
              />
            </div>
          </section>
        )}
      </div>
    )
  }
}
